import React, { FC, useMemo } from 'react'
import { Grid, Button } from '@material-ui/core'
import { Form as FinalForm } from 'react-final-form'
import MySelect from '../../../control/select/select'
import MyTextfield from '../../../control/textfield/textfield'
import moment from 'moment'
import 'moment/locale/th'
import { HolidayRequest } from '../../../services/um/useSettingHoliday'
import useTranslate from '../../../i18n/useTranslate'
import { selectRequired, textRequired, validateEN } from '../../../utilities/validate'

interface Props {
    value?: HolidayRequest
    onChange: (values: HolidayRequest) => void
    onCancel: () => void
}

const daysInMonth = (month: number) => {
    if ([1, 3, 5, 7, 8, 10, 12].includes(month)) return 31
    if ([4, 6, 9, 11].includes(month)) return 30
    return 29
}

const HolidayForm: FC<Props> = ({ value, onChange, onCancel }) => {
    const translate = useTranslate()

    const currentLanguage: 'th' | 'en' = translate?.languageStore?.currentLanguage === 'en' ? 'en' : 'th'

    const monthOptions = useMemo(
        () =>
            Array.from({ length: 12 }, (_, i) => ({
                label: moment().month(i).locale(currentLanguage).format('MMMM'),
                value: i + 1,
            })),
        [currentLanguage],
    )

    return (
        <FinalForm onSubmit={onChange} initialValues={value}>
            {({ handleSubmit, values }) => {
                const dayOptions = values.month
                    ? Array.from({ length: daysInMonth(values.month) }, (_, i) => ({
                          label: i + 1,
                          value: i + 1,
                      }))
                    : []

                return (
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <MySelect
                                name="month"
                                label={translate.get('UM.SETTING_HOLIDAY.FORM.MONTH')}
                                size="small"
                                option={monthOptions}
                                placeholder={translate.get('UM.SETTING_HOLIDAY.FORM.MONTH')}
                                defaultValue={values.month ? [values.month] : undefined}
                                validates={[selectRequired(translate.get('MESSAGE.M016'))]}
                                required
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <MySelect
                                name="day"
                                label={translate.get('UM.SETTING_HOLIDAY.FORM.DAY')}
                                size="small"
                                option={dayOptions}
                                placeholder={translate.get('UM.SETTING_HOLIDAY.FORM.DAY')}
                                disabled={!values.month}
                                defaultValue={values.day ? [values.day] : undefined}
                                validates={[selectRequired(translate.get('MESSAGE.M016'))]}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <MyTextfield
                                name="nameTH"
                                materialInputProps={{
                                    label: `${translate.get('UM.SETTING_HOLIDAY.FORM.HOLIDAY_NAME')}​ (${translate.get('LABEL.THAI')}​)`,
                                    placeholder: `${translate.get('UM.SETTING_HOLIDAY.FORM.HOLIDAY_NAME')}​ (${translate.get('LABEL.THAI')}​)`,
                                    size: 'small',
                                }}
                                validates={[textRequired(translate.get('MESSAGE.M001'))]}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <MyTextfield
                                name="nameEN"
                                materialInputProps={{
                                    label: `${translate.get('UM.SETTING_HOLIDAY.FORM.HOLIDAY_NAME')}​ (${translate.get('LABEL.ENG')}​)`,
                                    placeholder: `${translate.get('UM.SETTING_HOLIDAY.FORM.HOLIDAY_NAME')}​ (${translate.get('LABEL.ENG')}​)`,
                                    size: 'small',
                                }}
                                validates={[
                                    textRequired(translate.get('MESSAGE.M015')),
                                    validateEN(translate.get('MESSAGE.M032')),
                                ]}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} container justifyContent="center" spacing={1}>
                            <Grid item>
                                <Button onClick={onCancel} variant="outlined" color="secondary">
                                    {translate.get('BUTTON.CANCEL')}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button variant="contained" color="primary" onClick={handleSubmit}>
                                    {translate.get('BUTTON.SAVE_2')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                )
            }}
        </FinalForm>
    )
}

export default HolidayForm
